/**
 * How a person's name is rendered, in one place.
 *
 * WHY THIS EXISTS
 * ---------------
 * Three different name shapes reach the frontend and no caller sees all three:
 *
 *   `/auth/me`        → { fullName: 'Smith Admin' }        (no first/last at all)
 *   `/users`          → { first_name, last_name }          (snake_case rows)
 *   chat / job payloads → { firstName, lastName }          (camelCase entities)
 *
 * Every call site that hand-rolled this picked one or two of them and ended
 * with `|| user.email`, so the fallback fired on the shapes it had not thought
 * of and the UI rendered a login address where a person's name belongs — a
 * Pre-Job Meeting stored "admin@smithcasing.com" as its Meeting Leader for
 * exactly this reason. Note that checking BOTH casings of first/last is still
 * not enough: `/auth/me` carries neither, so a both-casing check falls through
 * to the email every single time for the signed-in user.
 *
 * The email is a last resort, not a name. `getUserDisplayName` exhausts every
 * known shape before it reaches for one.
 */

/** A user-ish object from any of the API's name shapes. */
export interface NamedUserLike {
  fullName?: string | null;
  full_name?: string | null;
  firstName?: string | null;
  first_name?: string | null;
  lastName?: string | null;
  last_name?: string | null;
  name?: string | null;
  email?: string | null;
  [key: string]: unknown;
}

/**
 * Best available display name for a user.
 *
 * Order is most-specific-wins: an explicit full name, then a first/last pair in
 * either casing, then a bare `name`, then the email. Returns `fallback`
 * (default `''`) when the object is missing or carries nothing renderable, so
 * callers can decide what an unknown person looks like.
 */
export function getUserDisplayName(user: NamedUserLike | null | undefined, fallback = ''): string {
  if (!user) return fallback;

  const full = (user.fullName ?? user.full_name ?? '').toString().trim();
  if (full) return full;

  const first = (user.firstName ?? user.first_name ?? '').toString().trim();
  const last = (user.lastName ?? user.last_name ?? '').toString().trim();
  const pair = [first, last].filter(Boolean).join(' ');
  if (pair) return pair;

  const bare = (user.name ?? '').toString().trim();
  if (bare) return bare;

  const email = (user.email ?? '').toString().trim();
  if (email) return email;

  return fallback;
}

/**
 * Did `getUserDisplayName` have to fall back to the email address?
 *
 * Lets a caller decide not to *store* an email in a field that means "person"
 * — rendering one is a cosmetic problem, persisting one is a data problem.
 */
export function isEmailDisplayName(user: NamedUserLike | null | undefined): boolean {
  if (!user) return false;
  const email = (user.email ?? '').toString().trim();
  return !!email && getUserDisplayName(user) === email;
}
